import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import Magnetic from "./motion/Magnetic";

const LINKS = [
  { to: "/", id: "home", label: "Home" },
  { to: "/about", id: "about", label: "About" },
  { to: "/programs", id: "programs", label: "Programmes" },
  { to: "/membership", id: "membership", label: "Membership" },
  { to: "/contact", id: "contact", label: "Contact" },
];

const EASE = [0.76, 0, 0.24, 1];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(y > 40);
    setHidden(y > prev && y > 160 && !open);
  });

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const light = open;

  return (
    <>
      <motion.header
        className={`fixed inset-x-0 top-0 z-[120] border-b transition-[background-color,border-color,box-shadow] duration-500 ${
          open
            ? "border-transparent bg-transparent"
            : scrolled
              ? "border-line bg-sand/90 shadow-[0_10px_30px_rgba(18,33,27,0.06)] backdrop-blur-xl"
              : "border-line/60 bg-sand/85 backdrop-blur-md"
        }`}
        animate={{ y: hidden ? "-100%" : "0%" }}
        transition={{ duration: 0.5, ease: EASE }}
        data-testid="site-nav"
      >
        <div className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-5 md:h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            aria-label="Veridian Education Foundation home"
            className="relative z-[130]"
          >
            <Logo light={light} />
          </Link>

          <nav className="hidden items-center gap-10 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `link-line pb-0.5 font-display text-[0.95rem] font-medium transition-colors duration-300 ${
                    isActive ? "text-pine after:!scale-x-100" : "text-ink hover:text-pine"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Magnetic strength={0.3}>
              <Link
                to="/membership"
                data-testid="nav-cta-member"
                className="btn-sweep btn-sweep-moss inline-flex items-center gap-2 rounded-md bg-pine px-4 py-2.5 font-display text-[0.9rem] font-semibold text-white transition-transform duration-500 hover:-translate-y-0.5"
              >
                Become a member <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Magnetic>
          </nav>

          <button
            className={`relative z-[130] flex h-11 w-11 flex-col items-center justify-center gap-1.5 lg:hidden ${
              light ? "text-white" : "text-pine"
            }`}
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <motion.span
              animate={{ rotate: open ? 45 : 0, y: open ? 4 : 0 }}
              className="block h-[1.5px] w-7 bg-current"
            />
            <motion.span
              animate={{ rotate: open ? -45 : 0, y: open ? -4 : 0 }}
              className="block h-[1.5px] w-7 bg-current"
            />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="fixed inset-0 z-[115] flex flex-col justify-between bg-pine px-5 pb-10 pt-28 text-white lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <ul className="space-y-2">
              {LINKS.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 font-display text-4xl font-medium ${
                        isActive ? "text-terra" : "text-white"
                      }`
                    }
                  >
                    <span className="text-xs font-sans font-semibold tracking-[0.3em] text-mintlight">0{i + 1}</span>
                    {l.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex items-end justify-between border-t border-sand/20 pt-6"
            >
              <Link
                to="/membership"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-cta-member"
                className="rounded-md bg-terra px-6 py-3.5 font-display text-sm font-semibold text-ink"
              >
                Become a member
              </Link>
              <img src="/brand/leaf.png" alt="" className="h-16 w-16 object-contain opacity-70" />
            </motion.div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
